import React, { useState } from 'react';
import { changeCleaning, CleaningAction, CleaningTodayItem } from '../../../../api/stays-alerts-client';
import { StaysApiError } from '../../../../api/stays-client';
import { AppButton } from '../../../button/app-button';
import { formatDayKey } from '../../../../function-utils/building-time';
import toast from '../../../../utils/toast';
import { formatMoney } from '../../../../utils/stays-money';
import { refusalText } from '../../dashboard/dashboard-stays-components/stays-labels';
import { Banner, BannerLine, BannerText } from './operations.styled';

/**
 * CLEANING TODAY (plan S8, SC-2; K-60, R-S8.16). One line per stay leaving or
 * arriving on the BUILDING's today with a booked or done cleaning (departing
 * first), at most three, then "and 2 more". "Mark as checked and cleaned"
 * completes it; "Undo" puts it back to booked. Amber while pending, green
 * when every line is done.
 */

const SHOWN = 3;

const range = (a: string, b: string): string => `${formatDayKey(a)}–${formatDayKey(b)}`;

export const cleaningLine = (it: CleaningTodayItem): string => {
  const fee = it.cleaning_fee_cents != null ? ` · ${formatMoney(it.cleaning_fee_cents, it.currency)}` : '';

  return it.cleaning_status === 'completed'
    ? `Cleaning checked today for ${it.first_name}'s stay (${range(it.check_in, it.check_out)})${fee}.`
    : `Cleaning is booked today for ${it.first_name}'s stay (${range(it.check_in, it.check_out)})${fee}. Please inspect the unit.`;
};

export const CleaningTodayBanner: React.FC<{
  items: CleaningTodayItem[];
  canManage: boolean;
  onChanged: () => void;
}> = ({ items, canManage, onChanged }) => {
  const [busy, setBusy] = useState<string | null>(null);
  if (!items.length) return null;
  const done = items.every(i => i.cleaning_status === 'completed');
  const shown = items.slice(0, SHOWN);
  const more = items.length - shown.length;

  const act = async (it: CleaningTodayItem, action: CleaningAction) => {
    if (busy) return;
    setBusy(it.reservation_id);
    try {
      await changeCleaning(it.reservation_id, { action, version: it.version });
      onChanged();
    } catch (e) {
      toast.error(refusalText(e as StaysApiError, 'Could not change the cleaning. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Banner $done={done} aria-label="Cleaning today" data-testid="cleaning-today">
      {shown.map(it => (
        <BannerLine key={`${it.reservation_id}-${it.role}`} data-reservation={it.reservation_id} data-role={it.role}>
          <BannerText>{cleaningLine(it)}</BannerText>
          {canManage ? (
            it.cleaning_status === 'completed' ? (
              <AppButton type="button" variant="ghost" size="compact" loading={busy === it.reservation_id} loadingLabel="Undoing"
                aria-label={`Undo: cleaning checked for ${it.first_name}'s stay`} onClick={() => void act(it, 'reopen')}>
                Undo
              </AppButton>
            ) : (
              <AppButton type="button" variant="secondary" size="compact" loading={busy === it.reservation_id}
                loadingLabel="Saving" onClick={() => void act(it, 'complete')}>
                Mark as checked and cleaned
              </AppButton>
            )
          ) : null}
        </BannerLine>
      ))}
      {more > 0 ? <BannerText>and {more} more</BannerText> : null}
    </Banner>
  );
};
